import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ImageIcon, Link2, Plus, Trash2, Users } from 'lucide-react';
import { useT } from '../lib/i18n';
import { api, avatarUrl, roomCoverUrl, thumbUrl } from '../lib/api';
import { shrinkImage } from '../lib/shrinkImage';
import { clock } from '../lib/format';
import { toast, useSession } from '../lib/session';
import { useMediaList } from '../lib/live';
import { useEnter } from '../motion/gsap';
import { Avatar, Modal, ModalTitle } from '../ui';
import { InviteDialog } from '../features/room/InviteDialog';
import type { RoomRecord } from '../lib/types';
import { joinRoom, useActiveRoom } from '../features/room/session';

export function RoomsPage() {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const active = useActiveRoom((s) => s.roomId);
  const user = useSession((s) => s.user);
  const [params, setParams] = useSearchParams();
  const [rooms, setRooms] = useState<RoomRecord[] | null>(null);
  const [invite, setInvite] = useState<RoomRecord | null>(null);
  const [coverFor, setCoverFor] = useState<RoomRecord | null>(null);
  const creating = params.get('new') === '1';
  useEnter(ref, [rooms === null]);

  const load = () => api.rooms().then(setRooms).catch(() => setRooms([]));
  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  const remove = async (r: RoomRecord) => {
    if (!confirm(`Delete room “${r.name}”?`)) return;
    await api.deleteRoom(r.id).catch((e) => toast(e.message, 'red'));
    load();
  };

  return (
    <div className="stack" ref={ref}>
      <section className="card" data-anim>
        <div className="card-head" style={{ alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <h2 className="card-title">{t('rooms.title')}</h2>
            <div className="card-sub">{t('rooms.sub')}</div>
          </div>
          <button className="btn btn-black" onClick={() => setParams({ new: '1' })}>
            <Plus size={15} /> {t('btn.newRoom')}
          </button>
        </div>
        {rooms && rooms.length === 0 && <div className="empty">{t('rooms.empty')}</div>}
        <div className="room-tiles">
          {rooms?.map((r) => {
            const peers = r.live?.peers ?? [];
            const st = r.live?.state;
            const thumb = roomCoverUrl(r) ?? ((r.coverMedia ?? r.media) && thumbUrl((r.coverMedia ?? r.media)!));
            return (
              <article key={r.id} className="room-tile" data-anim>
                <button className="room-tile-art" onClick={() => joinRoom(r.id, nav)} style={{ backgroundImage: thumb ? `url(${thumb})` : undefined }}>
                  <span className="room-tile-status">
                    {active === r.id ? (
                      <span className="glass-chip">
                        <span className="dot dot-green" /> {t('rooms.youreIn')}
                      </span>
                    ) : peers.length ? (
                      <span className="glass-chip">
                        <span className="dot dot-accent" />
                        {st?.status === 'playing' ? 'Playing' : st?.status === 'waiting' ? 'Waiting' : 'Paused'}
                        {st && r.media ? ` · ${clock(st.position)}` : ''}
                      </span>
                    ) : (
                      <span className="glass-chip">{t('rooms.idle')}</span>
                    )}
                  </span>
                </button>
                <div className="room-tile-body">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="cell-main" style={{ fontSize: 14 }}>
                      {r.name}
                    </div>
                    <div className="cell-sub" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.media?.title ?? '—'}
                    </div>
                  </div>
                  <div className="avatars">
                    {peers.slice(0, 4).map((p) => (
                      <Avatar key={p.id} name={p.name} src={avatarUrl(p)} />
                    ))}
                    {!peers.length && (
                      <span className="muted row" style={{ gap: 4, fontSize: 12 }}>
                        <Users size={13} />0
                      </span>
                    )}
                  </div>
                </div>
                <div className="room-tile-actions">
                  <button className="btn btn-black btn-sm" onClick={() => joinRoom(r.id, nav)}>
                    {active === r.id ? t('room.backToRoom') : t('rooms.open')}
                  </button>
                  <button className="btn btn-white btn-sm" onClick={() => setInvite(r)}>
                    <Link2 size={13} /> {t('btn.invite')}
                  </button>
                  <button className="btn btn-white btn-sm btn-icon" onClick={() => setCoverFor(r)} aria-label="Change cover" title="Change cover">
                    <ImageIcon size={13} />
                  </button>
                  {(user?.host || r.createdBy === user?.id) && (
                    <button className="btn btn-white btn-sm btn-icon" onClick={() => remove(r)} aria-label={t('btn.delete')}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
      <CreateRoomModal open={creating} onClose={() => setParams({})} />
      <CoverModal room={coverFor} onClose={() => setCoverFor(null)} onSaved={load} />
      {invite && <InviteDialog roomId={invite.id} roomName={invite.name} onClose={() => setInvite(null)} />}
    </div>
  );
}

function CreateRoomModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();
  const media = useMediaList((s) => s.items).filter((m) => m.playable);
  const refresh = useMediaList((s) => s.refresh);
  const [name, setName] = useState('');
  const [mediaId, setMediaId] = useState('');
  const [busy, setBusy] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    refresh();
    nameInput.current?.focus(); // a dialog moves focus into itself
  }, [open, refresh]);

  const create = async () => {
    setBusy(true);
    try {
      const room = await api.createRoom(name || 'Movie night', mediaId || undefined);
      onClose();
      joinRoom(room.id, nav);
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>{t('btn.newRoom')}</ModalTitle>
      <p className="muted" style={{ margin: '0 0 18px', fontSize: 13 }}>
        {t('rooms.sub')}
      </p>
      <div className="field" style={{ marginBottom: 14 }}>
        <label htmlFor="room-name">{t('rooms.name')}</label>
        <input id="room-name" className="input" value={name} placeholder="Movie night" onChange={(e) => setName(e.target.value)} ref={nameInput} />
      </div>
      <div className="field">
        <span className="field-label" id="room-pick">
          {t('rooms.pick')}
        </span>
        <div role="group" aria-labelledby="room-pick" style={{ maxHeight: 240, overflowY: 'auto', display: 'grid', gap: 2 }}>
          <button className={`media-pick${mediaId === '' ? ' on' : ''}`} onClick={() => setMediaId('')}>
            <div className="thumb" />
            <span className="muted">{t('rooms.nothing')}</span>
          </button>
          {media.map((m) => (
            <button key={m.id} className={`media-pick${mediaId === m.id ? ' on' : ''}`} onClick={() => setMediaId(m.id)}>
              <div className="thumb" style={{ backgroundImage: thumbUrl(m) ? `url(${thumbUrl(m)})` : undefined }} />
              <div style={{ minWidth: 0 }}>
                <div className="cell-main">{m.title}</div>
                <div className="cell-sub">{clock(m.duration)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
        <button className="btn btn-ghost" onClick={onClose}>
          {t('btn.cancel')}
        </button>
        <button className="btn btn-black" disabled={busy} onClick={create}>
          {t('btn.create')}
        </button>
      </div>
    </Modal>
  );
}

function CoverModal({ room, onClose, onSaved }: { room: RoomRecord | null; onClose: () => void; onSaved: () => void }) {
  const media = useMediaList((s) => s.items).filter((m) => m.playable);
  const refresh = useMediaList((s) => s.refresh);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (room) refresh();
  }, [room, refresh]);

  const pick = async (mediaId: string) => {
    if (!room) return;
    setBusy(true);
    try {
      await api.updateRoomCover(room.id, mediaId);
      onSaved();
      onClose();
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File | undefined) => {
    if (!room || !file) return;
    setBusy(true);
    try {
      await api.uploadRoomCover(room.id, await shrinkImage(file, 1920));
      onSaved();
      onClose();
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy(false);
    }
  };

  const current = room?.coverImage ? 'upload' : (room?.coverMediaId ?? '');
  return (
    <Modal open={!!room} onClose={onClose}>
      <ModalTitle>Change cover</ModalTitle>
      <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
        Upload a picture, or pick a title whose poster shows on this room's card.
      </p>
      <label className={`btn btn-black${busy ? ' disabled' : ''}`} style={{ width: '100%', marginBottom: 12 }}>
        <ImageIcon size={14} /> {busy ? 'Saving…' : 'Upload a picture'}
        <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      <div style={{ maxHeight: 320, overflowY: 'auto', display: 'grid', gap: 2 }}>
        <button disabled={busy} className={`media-pick${current === '' ? ' on' : ''}`} onClick={() => pick('')}>
          <div className="thumb" />
          <span className="muted">Use the title that is playing</span>
        </button>
        {media.map((m) => (
          <button key={m.id} disabled={busy} className={`media-pick${current === m.id ? ' on' : ''}`} onClick={() => pick(m.id)}>
            <div className="thumb" style={{ backgroundImage: thumbUrl(m) ? `url(${thumbUrl(m)})` : undefined }} />
            <div style={{ minWidth: 0 }}>
              <div className="cell-main">{m.title}</div>
              <div className="cell-sub">{clock(m.duration)}</div>
            </div>
          </button>
        ))}
      </div>
    </Modal>
  );
}
